import { Emitter } from "../../../base/common/events.js";
import { IWebGLService } from "../common/webgl.js";
import { IUserConfiguration } from "../../../editor/services/user-configuration/common/userConfiguration.js";
import { DrawTimer, type PassTiming } from "../common/drawTimer.js";
import { describeTimingSupport, type TimingSupport } from "../common/gpuTimingSupport.js";
import { describeRenderPass } from "../common/passNames.js";
import { isEditorInternalRender, isEditorInternalScene } from "../common/editorScene.js";
import type { GpuTimingFrame, GpuTimingResult, IGpuTimingService, PassSummary } from "../common/gpuTiming.js";

export { isEditorInternalScene };

/** What a pass IS, beyond its number. */
export interface PassInfo {
    readonly label: string;
    /** uuid of the scene it draws — how the render graph finds this pass again */
    readonly sceneId?: string;
}

/**
 * Stable pass ids for one measurement.
 *
 * Numbering by POSITION in the frame renumbers everything after any render that
 * only happens sometimes — an editor preview, an effect that runs on change —
 * and a 30-frame measurement then reports the same scene as a dozen passes that
 * each ran "1/30". A pass is the scene+camera being drawn, so that is the key.
 */
export class PassNumbering {

    private readonly _ids = new Map<string, number>();
    private readonly _info = new Map<number, PassInfo>();
    private _seenThisFrame = new Map<string, number>();
    /** the page has shown us where its frames end — our own boundary steps aside */
    private _pageEndsItsFrames = false;

    /**
     * Our measured-frame boundary. It only resets the repeat count while the
     * page has never drawn to the canvas (an offscreen/XR pipeline); once it
     * has, that signal is exact and ours would cut a frame in half.
     */
    beginFrame(): void {
        if (!this._pageEndsItsFrames) this._seenThisFrame = new Map();
    }

    idFor(scene: unknown, camera: unknown, target?: unknown): number {
        const pair = `${uuidOf(scene) ?? "scene?"}:${uuidOf(camera) ?? "camera?"}:${targetShape(target)}`;
        // Nth time this exact pass is drawn in the page's CURRENT frame — a
        // pipeline that draws its scene three times into three identically
        // shaped buffers is three passes, not one bucket with three cards
        // fighting over it.
        const repeat = this._seenThisFrame.get(pair) ?? 0;
        this._seenThisFrame.set(pair, repeat + 1);
        const key = `${pair}#${repeat}`;
        const id = this._idFor(key, scene);

        // A render with no target bound goes to the canvas: that is the page
        // telling us its frame is over, which no rAF of ours can know.
        if (!target) {
            this._pageEndsItsFrames = true;
            this._seenThisFrame = new Map();
        }
        return id;
    }

    private _idFor(key: string, scene: unknown): number {
        const existing = this._ids.get(key);
        if (existing !== undefined) return existing;

        const id = this._ids.size;
        this._ids.set(key, id);
        this._info.set(id, { label: describeRenderPass(scene) ?? `Pass ${id + 1}`, sceneId: uuidOf(scene) });
        return id;
    }

    info(): ReadonlyMap<number, PassInfo> { return this._info; }
}

/**
 * What KIND of target a pass draws into — size, format and type, never identity.
 *
 * A depth prepass and the colour pass share a scene and a camera and differ only
 * here. Keying on the target's uuid instead would split a ping-pong post chain,
 * whose two buffers are the same stage on alternating frames.
 */
const targetShape = (target: unknown): string => {
    const rt = target as { width?: unknown, height?: unknown, texture?: { type?: unknown, format?: unknown } } | null;
    if (!rt || typeof rt !== "object") return "canvas";
    return `${rt.width}x${rt.height}:${rt.texture?.type}:${rt.texture?.format}`;
};

const uuidOf = (value: unknown): string | undefined => {
    const uuid = (value as { uuid?: unknown } | null)?.uuid;
    return typeof uuid === "string" && uuid.length > 0 ? uuid : undefined;
};

/** Bucket the editor's own renders land in, so they can be dropped whole. */
const EDITOR_PASS = -1;
/** Bucket for shadow-map rendering — see the shadowMap hook in measureWebGL. */
const SHADOW_PASS = -2;
/** Draws issued outside any render() call — they belong to no pass we can name. */
export const UNATTRIBUTED_PASS = -3;

/** Default frames per measurement — ~0.5s at 60 fps, enough to average out jitter. */
const DEFAULT_FRAMES = 30;

interface RendererLike {
    getContext?(): unknown;
    backend?: {
        trackTimestamp?: boolean;
        /** three's per-type pools; `render.timestamps` holds the PER-PASS split */
        timestampQueryPool?: { render?: { timestamps?: ReadonlyMap<string, number>, frames?: number[] } };
    };
    resolveTimestampsAsync?(type: string): Promise<unknown>;
    info?: { render?: { timestamp?: number, drawCalls?: number } };
}

const nextFrame = () => new Promise<void>(resolve => requestAnimationFrame(() => resolve()));

/**
 * GPU timing, on demand.
 *
 * Everything it touches belongs to the PAGE — the draw calls it wraps (WebGL) or
 * the tracking flag it flips (WebGPU) — so both are restored before it returns,
 * including when the measurement throws.
 */
export class GpuTimingService implements IGpuTimingService {

    declare readonly _serviceBrand: undefined;

    private _last: GpuTimingResult | null = null;
    get last(): GpuTimingResult | null { return this._last; }

    constructor(
        @IWebGLService private readonly webgl: IWebGLService,
        // optional only for direct construction (tests); every product registers it
        @IUserConfiguration private readonly config?: IUserConfiguration,
    ) { }

    /**
     * The renderer to measure.
     *
     * A page can have SEVERAL — a WebGLRenderer and a WebGPURenderer side by side
     * is a real combination — and `getRenderer()` answers with whichever rendered
     * last. Prefer one we can actually time, so a second, idle renderer cannot
     * make the whole feature look unsupported.
     */
    private renderer(): RendererLike | null {
        const engine = this.webgl.getEngine("threejs") ?? this.webgl.getAllEngines()[0] ?? null;
        const last = (engine?.getRenderer?.() as RendererLike | undefined) ?? null;
        const all = (engine?.getRenderers?.() ?? []) as RendererLike[];
        const candidates = [last, ...all].filter(Boolean) as RendererLike[];
        return candidates.find(r => describeTimingSupport(r).supported) ?? last ?? candidates[0] ?? null;
    }

    support(): TimingSupport {
        // the user's switch comes first: "off" is an answer, not an absence
        if (this.config?.gpuTimestamps === false) {
            return { supported: false, backend: describeTimingSupport(this.renderer()).backend, reason: "GPU timing is turned off in Settings." };
        }
        return describeTimingSupport(this.renderer());
    }

    private readonly _onDidMeasure = new Emitter<GpuTimingResult | null>();
    readonly onDidMeasure = this._onDidMeasure.event;

    async measure(frames = DEFAULT_FRAMES): Promise<GpuTimingResult | null> {
        const renderer = this.renderer();
        const support = this.support();
        if (!support.supported || !renderer) { this._onDidMeasure.fire(null); return null; }

        const count = Math.max(2, Math.min(300, frames));
        const measured = support.backend === "webgpu"
            ? await this.measureWebGPU(renderer, count)
            : await this.measureWebGL(renderer, count);

        if (!measured || measured.frames.length === 0) { this._onDidMeasure.fire(null); return null; }
        this._last = measured;
        this._onDidMeasure.fire(measured);
        return measured;
    }

    /** WebGL: a timer query around every draw, summed per frame. */
    private async measureWebGL(renderer: RendererLike, frameCount: number): Promise<GpuTimingResult | null> {
        const gl = renderer.getContext?.() as Record<string, any> | null;
        const ext = gl?.getExtension?.("EXT_disjoint_timer_query_webgl2") ?? gl?.getExtension?.("EXT_disjoint_timer_query");
        if (!gl || !ext) return null;

        const timer = new DrawTimer(gl as never, ext.TIME_ELAPSED_EXT ?? 0x88BF);

        // Wrap the draw entry points, then put the context back EXACTLY as it was:
        // these live on WebGL2RenderingContext.prototype, so assigning creates an own
        // property that would shadow it forever. Restore the descriptor, or delete.
        const DRAWS = ["drawElements", "drawArrays", "drawElementsInstanced", "drawArraysInstanced"] as const;
        const original = new Map<string, { fn: Function, own: PropertyDescriptor | undefined }>();
        for (const name of DRAWS) {
            const fn = gl[name];
            if (typeof fn !== "function") continue;
            original.set(name, { fn, own: Object.getOwnPropertyDescriptor(gl, name) });
            Object.defineProperty(gl, name, {
                value: (...args: unknown[]) => timer.timeDraw(() => fn.apply(gl, args)),
                writable: true, configurable: true,
            });
        }
        const restore = () => {
            for (const [name, entry] of original) {
                if (entry.own) Object.defineProperty(gl, name, entry.own);
                else delete gl[name];
            }
            if (typeof originalRender === "function") {
                if (renderOwn) Object.defineProperty(renderer, "render", renderOwn);
                else delete (renderer as { render?: unknown }).render;
            }
            if (shadowMap && typeof originalShadowRender === "function") {
                if (shadowOwn) Object.defineProperty(shadowMap, "render", shadowOwn);
                else delete (shadowMap as { render?: unknown }).render;
            }
        };

        // three calls render() ONCE PER PASS — the same definition the render stats
        // use — so wrapping it is exact pass attribution without the engine having
        // to report anything. Restored with the draw calls below.
        let openPass = 0;
        let currentFrame = 0;
        const numbering = new PassNumbering();
        const info = new Map<number, PassInfo>();
        const renderOwn = Object.getOwnPropertyDescriptor(renderer, "render");
        const originalRender = (renderer as { render?: Function }).render;
        if (typeof originalRender === "function") {
            Object.defineProperty(renderer, "render", {
                value: (scene: { name?: string, type?: string }, camera: unknown) => {
                    // OUR OWN renders (previews, overlays) are flagged HideInHierarchy
                    // and left out of the page's stats — count them here and the
                    // measurement reports the inspector's cost as the page's.
                    if (isEditorInternalRender(scene, camera)) {
                        openPass = EDITOR_PASS;
                        timer.beginPass(currentFrame, EDITOR_PASS);
                        return originalRender.call(renderer, scene, camera);
                    }
                    // the bound target is part of what makes a pass distinct — see targetShape
                    openPass = numbering.idFor(scene, camera, (renderer as { getRenderTarget?(): unknown }).getRenderTarget?.());
                    timer.beginPass(currentFrame, openPass);
                    return originalRender.call(renderer, scene, camera);
                },
                writable: true, configurable: true,
            });
        }

        // Shadow maps never come through render(): three draws them INSIDE it, from
        // renderer.shadowMap.render(). Without this hook their cost lands in whatever
        // pass is open — on a shadow-heavy scene, the most expensive thing in the
        // frame hides inside "Scene".
        const shadowMap = (renderer as { shadowMap?: { render?: Function } }).shadowMap;
        const shadowOwn = shadowMap ? Object.getOwnPropertyDescriptor(shadowMap, "render") : undefined;
        const originalShadowRender = shadowMap?.render;
        if (shadowMap && typeof originalShadowRender === "function") {
            Object.defineProperty(shadowMap, "render", {
                value: function (...args: unknown[]) {
                    const resumeAt = openPass;
                    timer.beginPass(currentFrame, SHADOW_PASS);
                    info.set(SHADOW_PASS, { label: "Shadows" });
                    try { return originalShadowRender.apply(this, args); }
                    finally { timer.beginPass(currentFrame, resumeAt); } // back to the pass it interrupted
                },
                writable: true, configurable: true,
            });
        }

        const passes: PassTiming[] = [];
        try {
            for (let frame = 0; frame < frameCount; frame++) {
                currentFrame = frame;
                numbering.beginFrame(); // only bites while the page never composites
                timer.beginPass(frame, UNATTRIBUTED_PASS);
                await nextFrame();
                timer.drain();
                passes.push(...timer.takeResults());
            }
            // the last frames' queries are still on the GPU; give them a moment
            for (let wait = 0; wait < 20 && timer.pending > 0; wait++) {
                await new Promise<void>(resolve => setTimeout(resolve, 2));
                timer.drain();
            }
            passes.push(...timer.takeResults());
        }
        finally {
            restore();
            timer.dispose();
        }

        const byFrame = new Map<number, GpuTimingFrame>();
        for (const pass of passes) {
            if (pass.pass === EDITOR_PASS) continue;
            const existing = byFrame.get(pass.frame);
            byFrame.set(pass.frame, {
                frame: pass.frame,
                gpuMs: round((existing?.gpuMs ?? 0) + pass.gpuMs),
                drawCalls: (existing?.drawCalls ?? 0) + pass.draws,
            });
        }
        const pagePasses = passes.filter(p => p.pass !== EDITOR_PASS); // shadows stay: the page pays for them
        for (const [id, entry] of numbering.info()) info.set(id, entry);
        return summarize("webgl", [...byFrame.values()].sort((a, b) => a.frame - b.frame), summarizePasses(pagePasses, info));
    }

    /**
     * WebGPU: read the timestamps the app already asked for.
     *
     * We do NOT enable tracking ourselves — see describeTimingSupport. three keeps
     * the query set on cached pass descriptors, so turning it on from outside
     * leaves the page throwing validation errors once that pool is disposed.
     */
    private async measureWebGPU(renderer: RendererLike, frameCount: number): Promise<GpuTimingResult | null> {
        // watch render() only to LEARN THE NAMES: three stamps each timestamp with
        // the render call it came from, so the scene passed to the n-th call names
        // the n-th pass. Restored in the finally below — this patches a page we do
        // not own.
        const callInfo = new Map<number, PassInfo>();
        const renderOwn = Object.getOwnPropertyDescriptor(renderer, "render");
        const originalRender = (renderer as { render?: Function }).render;
        if (typeof originalRender === "function") {
            Object.defineProperty(renderer, "render", {
                value: function (scene: unknown, camera: unknown) {
                    // frameCalls is incremented INSIDE the call, so the uid this
                    // render will be stamped with is the next number
                    const call = ((renderer.info?.render as { frameCalls?: number })?.frameCalls ?? 0) + 1;
                    if (!callInfo.has(call) && !isEditorInternalRender(scene, camera)) {
                        const named = describeRenderPass(scene);
                        if (named) callInfo.set(call, { label: named, sceneId: uuidOf(scene) });
                    }
                    return originalRender.call(renderer, scene, camera);
                },
                writable: true, configurable: true,
            });
        }

        const frames: GpuTimingFrame[] = [];
        try {
            for (let frame = 0; frame < frameCount; frame++) {
                await nextFrame();
                try { await renderer.resolveTimestampsAsync?.("render"); }
                catch { /* nothing resolved this frame */ }
                const render = renderer.info?.render;
                const ms = typeof render?.timestamp === "number" ? render.timestamp : 0;
                // a frame with nothing resolved is SKIPPED, not recorded as 0 — zeros
                // would drag the average toward "the GPU is free"
                if (ms > 0) frames.push({ frame, gpuMs: round(ms), drawCalls: render?.drawCalls ?? 0 });
            }
        }
        finally {
            if (typeof originalRender === "function") {
                if (renderOwn) Object.defineProperty(renderer, "render", renderOwn);
                else delete (renderer as { render?: unknown }).render;
            }
        }

        // the per-pass split three keeps but does not expose: read the newest frames
        // straight out of the pool's map rather than trusting its `frames` array
        const pool = renderer.backend?.timestampQueryPool?.render;
        let passes: PassSummary[] = [];
        if (pool?.timestamps) {
            const measured = latestFrames(pool.timestamps, frameCount);
            passes = summarizePasses(
                passesFromTimestamps(pool.timestamps, measured),
                passLabelsFromTimestamps(pool.timestamps, measured, callInfo),
            );
        }
        return summarize("webgpu", frames, passes);
    }
}

const round = (ms: number) => Math.round(ms * 1000) / 1000;


/**
 * The newest `count` frames present in a timestamp map.
 *
 * The pool's own `frames` array is only assigned by a resolve that RAN — ours can
 * early-return while another is in flight (the extension drains the pool every
 * 500 ms so three does not exhaust its queries), and then the array describes
 * someone else's window. The keys carry the frame themselves —
 * "r:<call>:<context>:f<frame>" (Backend.js:491) — so read it from there.
 */
export function latestFrames(timestamps: ReadonlyMap<string, number>, count: number): number[] {
    const frames = new Set<number>();
    for (const uid of timestamps.keys()) {
        const match = /:f(\d+)$/.exec(uid);
        if (match) frames.add(Number(match[1]));
    }
    return [...frames].sort((a, b) => a - b).slice(-Math.max(0, count));
}

/**
 * WebGPU's per-pass split, out of the pool's `timestamps` map.
 *
 * `info.render.timestamp` is the SUM of a frame's passes — three adds them up and
 * drops the detail. The pool keeps it: keys are "<renderContext>:f<frame>", values
 * are milliseconds (WebGPUTimestampQueryPool.js:221). Reading it changes nothing,
 * which matters because we do not enable tracking ourselves.
 *
 * Pass INDEX is assigned per render context in first-seen order, so the same
 * context stays the same pass across frames — otherwise the averages would mix a
 * shadow pass with a post pass.
 */
export function passesFromTimestamps(timestamps: ReadonlyMap<string, number>, frames: readonly number[]): PassTiming[] {
    // The ":f<frame>" suffix comes from the three WE bundle; the PAGE bundles its
    // own, and older versions key the map by context alone. Without this the
    // parser dropped every entry and the table stayed empty while totals worked.
    const tagged = [...timestamps.keys()].some(uid => /:f\d+$/.test(uid));
    if (!tagged) {
        let index = 0;
        return [...timestamps].map(([, ms]) => ({ frame: 0, pass: index++, draws: 0, gpuMs: round(ms) }));
    }
    const out: PassTiming[] = [];
    for (const entry of indexContexts(timestamps, frames)) {
        out.push({ frame: entry.frame, pass: entry.pass, draws: 0, gpuMs: round(entry.ms) });
    }
    return out.sort((a, b) => (a.frame - b.frame) || (a.pass - b.pass));
}

/** Timestamp entries with the pass index they belong to, in map order. */
function indexContexts(timestamps: ReadonlyMap<string, number>, frames: readonly number[]) {
    const wanted = new Set(frames);
    const passIndex = new Map<string, number>();
    const out: { frame: number, pass: number, context: string, ms: number }[] = [];
    for (const [uid, ms] of timestamps) {
        const match = /^(.*):f(\d+)$/.exec(uid);
        if (!match) continue;
        const frame = Number(match[2]);
        if (!wanted.has(frame)) continue;
        const context = match[1];
        if (!passIndex.has(context)) passIndex.set(context, passIndex.size);
        out.push({ frame, pass: passIndex.get(context)!, context, ms });
    }
    return out;
}

/**
 * A name for each WebGPU pass, from the render calls we watched.
 *
 * The key is "r:<call>:<context>:f<frame>" (Backend.js:491) and a call is one
 * `renderer.render(scene, camera)` — so the scene names collected while measuring
 * line up with the timestamps by that number. Without it the table says "Pass 5"
 * next to a graph that says "Pass: RTT" for the very same work.
 */
export function passLabelsFromTimestamps(
    timestamps: ReadonlyMap<string, number>,
    frames: readonly number[],
    callInfo: ReadonlyMap<number, PassInfo>,
): Map<number, PassInfo> {
    const info = new Map<number, PassInfo>();
    for (const entry of indexContexts(timestamps, frames)) {
        if (info.has(entry.pass)) continue;
        const call = /^r:(\d+):/.exec(entry.context);
        const known = call ? callInfo.get(Number(call[1])) : undefined;
        if (known) info.set(entry.pass, known);
    }
    return info;
}

/**
 * Per-pass averages across the frames measured, dearest first.
 *
 * "The frame costs 4 ms" is not actionable; "the shadow pass costs 3 of them"
 * is. Averaged rather than summed so the numbers stay comparable to the
 * per-frame figure the row shows.
 */
export function summarizePasses(passes: readonly PassTiming[], info: ReadonlyMap<number, PassInfo>): PassSummary[] {
    const byPass = new Map<number, { ms: number, draws: number, frames: number }>();
    for (const pass of passes) {
        if (pass.pass === UNATTRIBUTED_PASS) continue; // real work, but not a pass
        const entry = byPass.get(pass.pass) ?? { ms: 0, draws: 0, frames: 0 };
        entry.ms += pass.gpuMs;
        entry.draws += pass.draws;
        entry.frames++;
        byPass.set(pass.pass, entry);
    }
    const out: PassSummary[] = [];
    for (const [pass, entry] of byPass) {
        out.push({
            pass,
            label: info.get(pass)?.label ?? `Pass ${pass + 1}`,
            ...(info.get(pass)?.sceneId ? { sceneId: info.get(pass)!.sceneId } : {}),
            avgMs: round(entry.ms / entry.frames),
            avgDraws: Math.round(entry.draws / entry.frames),
            frames: entry.frames,
        });
    }
    return out.sort((a, b) => b.avgMs - a.avgMs);
}

function summarize(backend: "webgl" | "webgpu", frames: GpuTimingFrame[], passes: PassSummary[]): GpuTimingResult | null {
    if (frames.length === 0) return null;
    const times = frames.map(f => f.gpuMs);
    const draws = frames.map(f => f.drawCalls);
    const avg = (xs: number[]) => xs.reduce((a, b) => a + b, 0) / xs.length;
    return {
        backend,
        framesMeasured: frames.length,
        avgMs: round(avg(times)),
        minMs: round(Math.min(...times)),
        maxMs: round(Math.max(...times)),
        avgDrawCalls: Math.round(avg(draws)),
        at: Date.now(),
        frames,
        passes,
    };
}
